import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, ChevronDown, Zap } from "lucide-react";

const formats = [
  { id: 'jpg', label: 'JPG/JPEG', color: 'bg-orange-500' },
  { id: 'png', label: 'PNG', color: 'bg-blue-500' },
  { id: 'webp', label: 'WebP', color: 'bg-green-500' },
  { id: 'heic', label: 'HEIC', color: 'bg-rose-500' },
  { id: 'svg', label: 'SVG', color: 'bg-emerald-500' },
  { id: 'bmp', label: 'BMP', color: 'bg-indigo-500' },
  { id: 'tiff', label: 'TIFF', color: 'bg-purple-500' },
  { id: 'ico', label: 'ICO', color: 'bg-cyan-500' },
];

// Conversion routes mapping
const conversionRoutes = {
  'jpg-png': '/convert-jpg-to-png',
  'jpg-webp': '/convert-jpg-to-webp',
  'jpg-svg': '/convert-jpg-to-svg',
  'jpg-bmp': '/convert-jpg-to-bmp',
  'jpg-tiff': '/convert-jpg-to-tiff',
  'jpg-ico': '/convert-jpg-to-ico',
  'jpg-jpeg': '/convert-jpg-to-jpeg',
  'jpg-heic': '/convert-jpg-to-heic',
  'png-jpg': '/convert-png-to-jpg',
  'png-webp': '/convert-png-to-webp',
  'png-svg': '/convert-png-to-svg',
  'png-bmp': '/convert-png-to-bmp',
  'png-tiff': '/convert-png-to-tiff',
  'png-ico': '/convert-png-to-ico',
  'png-jpeg': '/convert-png-to-jpeg',
  'png-heic': '/convert-png-to-heic',
  'webp-png': '/convert-webp-to-png',
  'webp-jpg': '/convert-webp-to-jpg',
  'webp-svg': '/convert-webp-to-svg',
  'webp-bmp': '/convert-webp-to-bmp',
  'webp-tiff': '/convert-webp-to-tiff',
  'webp-ico': '/convert-webp-to-ico',
  'webp-jpeg': '/convert-webp-to-jpeg',
  'webp-heic': '/convert-webp-to-heic',
  'heic-jpg': '/convert-heic-to-jpg',
  'heic-jpeg': '/convert-heic-to-jpeg',
  'heic-png': '/convert-heic-to-png',
  'heic-webp': '/convert-heic-to-webp',
  'heic-svg': '/convert-heic-to-svg',
  'heic-bmp': '/convert-heic-to-bmp',
  'heic-tiff': '/convert-heic-to-tiff',
  'heic-ico': '/convert-heic-to-ico',
  'svg-png': '/convert-svg-to-png',
  'svg-jpg': '/convert-svg-to-jpg',
  'svg-jpeg': '/convert-svg-to-jpeg',
  'svg-webp': '/convert-svg-to-webp',
  'svg-bmp': '/convert-svg-to-bmp',
  'svg-tiff': '/convert-svg-to-tiff',
  'svg-ico': '/convert-svg-to-ico',
  'svg-heic': '/convert-svg-to-heic',
  'bmp-png': '/convert-bmp-to-png',
  'bmp-jpg': '/convert-bmp-to-jpg',
  'bmp-webp': '/convert-bmp-to-webp',
  'bmp-tiff': '/convert-bmp-to-tiff',
  'bmp-ico': '/convert-bmp-to-ico',
  'bmp-svg': '/convert-bmp-to-svg',
  'bmp-jpeg': '/convert-bmp-to-jpeg',
  'bmp-heic': '/convert-bmp-to-heic',
  'tiff-png': '/convert-tiff-to-png',
  'tiff-jpg': '/convert-tiff-to-jpg',
  'tiff-webp': '/convert-tiff-to-webp',
  'tiff-svg': '/convert-tiff-to-svg',
  'tiff-bmp': '/convert-tiff-to-bmp',
  'tiff-ico': '/convert-tiff-to-ico',
  'tiff-jpeg': '/convert-tiff-to-jpeg',
  'tiff-heic': '/convert-tiff-to-heic',
  'ico-png': '/convert-ico-to-png',
  'ico-jpg': '/convert-ico-to-jpg',
  'ico-webp': '/convert-ico-to-webp',
  'ico-svg': '/convert-ico-to-svg',
  'ico-bmp': '/convert-ico-to-bmp',
  'ico-tiff': '/convert-ico-to-tiff',
  'ico-jpeg': '/convert-ico-to-jpeg',
  'ico-heic': '/convert-ico-to-heic',
};

export default function QuickConvert() {
  const [isOpen, setIsOpen] = useState(false);
  const [sourceFormat, setSourceFormat] = useState(null);
  const [targetFormat, setTargetFormat] = useState(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Navigate when both formats selected
  useEffect(() => {
    if (sourceFormat && targetFormat && sourceFormat !== targetFormat) {
      const routeKey = `${sourceFormat}-${targetFormat}`;
      const route = conversionRoutes[routeKey];
      if (route) {
        navigate(route);
        setIsOpen(false);
        setSourceFormat(null);
        setTargetFormat(null);
      }
    }
  }, [sourceFormat, targetFormat, navigate]);

  const getAvailableTargets = () => {
    if (!sourceFormat) return formats;
    return formats.filter(f => {
      const routeKey = `${sourceFormat}-${f.id}`;
      return conversionRoutes[routeKey] && f.id !== sourceFormat;
    });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-800 hover:border-blue-400 text-blue-700 dark:text-blue-300"
        data-testid="quick-convert-trigger"
      >
        <Zap className="w-4 h-4" />
        <span className="font-medium">Quick Convert</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </Button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden z-[100]">
          {/* Header */}
          <div className="px-4 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
            <h3 className="font-semibold flex items-center gap-2">
              <Zap className="w-4 h-4" /> Quick Convert
            </h3>
            <p className="text-xs opacity-80 mt-0.5">Select source → target format</p>
          </div>

          {/* Format Selectors */}
          <div className="p-4">
            {/* Source Format */}
            <div className="mb-4">
              <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2 block">
                From (Source)
              </label>
              <div className="flex flex-wrap gap-2">
                {formats.map(format => (
                  <button
                    key={format.id}
                    onClick={() => {
                      setSourceFormat(format.id);
                      setTargetFormat(null);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      sourceFormat === format.id
                        ? `${format.color} text-white shadow-md scale-105`
                        : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }`}
                  >
                    {format.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Arrow */}
            {sourceFormat && (
              <div className="flex justify-center my-2">
                <ArrowRight className="w-5 h-5 text-slate-400" />
              </div>
            )}

            {/* Target Format */}
            {sourceFormat && (
              <div>
                <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2 block">
                  To (Target)
                </label>
                <div className="flex flex-wrap gap-2">
                  {getAvailableTargets().map(format => (
                    <button
                      key={format.id}
                      onClick={() => setTargetFormat(format.id)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        targetFormat === format.id
                          ? `${format.color} text-white shadow-md scale-105`
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                      }`}
                    >
                      {format.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Selected Preview */}
            {sourceFormat && targetFormat && sourceFormat !== targetFormat && (
              <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <div className="flex items-center justify-center gap-2 text-green-700 dark:text-green-300">
                  <Badge className={`${formats.find(f => f.id === sourceFormat)?.color} text-white`}>
                    {formats.find(f => f.id === sourceFormat)?.label}
                  </Badge>
                  <ArrowRight className="w-4 h-4" />
                  <Badge className={`${formats.find(f => f.id === targetFormat)?.color} text-white`}>
                    {formats.find(f => f.id === targetFormat)?.label}
                  </Badge>
                </div>
                <p className="text-xs text-center text-green-600 dark:text-green-400 mt-2">
                  Redirecting...
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 bg-slate-50 dark:bg-slate-900/50 border-t border-slate-200 dark:border-slate-700">
            <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
              70+ conversion combinations available
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
